import { PapiUser, PapiDamzSearchMessages } from '@wix/da-papi-types';
import { Database } from '@wix/da-papi-types/lib/supabase';
import emojiMap from '../emojiMap';

/**
 * Default reaction emoji IDs that appear in the reaction picker.
 * These are indices from emojiMap.ts array.
 * 👍 (thumbs up)
 * 💚 (green heart)
 * 😂 (face with tears of joy)
 * 😮 (face with open mouth)
 * 😢 (crying face)
 * 😠 (angry face)
 */
export const DEFAULT_REACTION_EMOJI_IDS = [
  458, 12, 278, 304, 329, 341,
] as const;

/**
 * Helper function to convert emoji_id (emojiMap.ts index) to unicode emoji string.
 */
export function getEmojiFromId(emoji_id: number): string {
  const hex = emojiMap[emoji_id];
  if (!hex) return '';
  return String.fromCodePoint(...hex.split('-').map(h => parseInt(h, 16)));
}

export enum FocusMessageStep {
  Idle = 'idle',
  Fetch = 'fetch',
  Render = 'render',
  Scrolling = 'scrolling',
  DoneScrolling = 'doneScrolling',
}

/**
 * Room window states for managing unread markers and user interaction
 */
export enum RoomWindowState {
  ActiveChatting = 'active_chatting',
  ScrolledUp = 'scrolled_up',
  FocusedAway = 'focused_away',
  OpenedAt = 'opened_at',
}

/**
 * Snapshot of room state for restoration when regaining focus
 */
export interface RoomStateSnapshot {
  windowState: RoomWindowState;
  unreadMessageCount: number;
}

/**
 * State management for message window
 */
export interface RoomUnreadState {
  /** Whether the room was just opened */
  isJustOpened: boolean;
  /** Number of unread messages in the room */
  unreadMessageCount: number;
  /** ID of the last message the user viewed */
  lastViewedMessageId?: string;
  /** ID of the last message that was successfully marked as viewed via RPC */
  lastMarkedMessageId?: string;
  /** Whether the window/tab is currently focused/active */
  isWindowFocused: boolean;
  /** Current window state */
  windowState: RoomWindowState;
  /** Snapshot of state before losing focus for restoration */
  previousStateSnapshot?: RoomStateSnapshot;
}

export type ReactionMap = { emoji_id: number; count: number; order: number }[];

export interface DraftMessage {
  ts: number;
  text: string;
  attachments: MessageAttachment[];
  isMessageAllowed?: boolean;
  editMessageId?: string;
}

/**
 * Log entry for a link that was already unfurled in the draft editor.
 * We keep the original href as well as the offsets reported by linkify-js
 * so we can identify the same occurrence even if the href string changes slightly.
 */
export interface UnfurlLogEntry {
  href: string;
  start: number;
  end: number;
  kind: 'video' | 'deviation';
}

/**
 * Because we load redux dynamically, we have to initialize a few things before our damz
 * are ready to be displayed. This enum is used to track those things (e.g. are translations initializing?)
 */
export enum InitializationStatus {
  /**
   * We're still waiting on something (e.g. code to execute, network request to finish)
   */
  Initializing = 'initializing',
  /**
   * Initialized and ready to go!
   */
  Success = 'success',
  /**
   * Most likely we failed to load a resource due to a network issue.
   */
  Failure = 'failure',
}

export interface DamzState {
  /**
   * Because we load damz and its resources dynamically, we have to keep track of those resources.
   */
  initialization: {
    isReduxInitialized: InitializationStatus;
    areTranslationsInitialized: InitializationStatus;
    online: boolean;
  };

  settings: {
    disableSounds: boolean;
    onlyWatch: boolean;
    status: 'uninitialized' | 'fetching' | 'idle';
    hasLegacyNotes?: boolean;
    hasLegacyChat?: boolean;
  };

  realtime: {
    token: string | undefined;
    expiresAt: number | undefined;
    waitingForToken: number | undefined;
  };

  altcha: {
    withMsgToken: string | undefined;
    withoutMsgToken: string | undefined;
  };

  activity: {
    // As rooms change we use this queue to batch UI updates into a reasonable rate
    roomsQueue: string[];
  };

  counter: {
    // The global counter used in the header
    conversations: number;
    requests: number;
  };

  drawer: {
    // Drawer filtering
    activeFilter: RoomFlagNames;
  };

  room: {
    // Flag that forces new chat UI
    newChatActive: boolean;

    // Current room_id
    activeRoomId: string | undefined;

    // We need to wait for the room data to be loaded before we can focus the message box
    // so that a11y text is read out properly
    isReadyToFocusMessageBox: boolean;

    // Prev room_id so we can clear the stream when moving away
    prevActiveRoom?: string | undefined;

    // The room item
    activeRoomItem?: RoomItem;

    focusMessageRequest: {
      messageId?: string;
      requiredFetch: boolean;
      step: FocusMessageStep;
    };

    dmCardData: {
      initStatus: InitializationStatus;
      deviationCount: number;
      watcherCount: number;
      isCurrentUserWatchingThisOne: boolean;
    };

    // Who is chatting in the room, this helps not send alerts when you are actively chatting
    presence?: {
      [room_id: string]: {
        [user_id: string]: { user_id: string; presence_ref: string };
      };
    };

    // Queue for marking things read so we don't overload servers
    markAsReadQueue?: string[];

    // The typing indicator text
    typingText?: string;

    // This is just to help clearing the editor without needing to pass
    // around the editor instance in redux
    sentMarker: number;

    // Error message from server
    sendError:
      | {
          errorDescription: string;
          errorKey: string;
        }
      | undefined;

    // Messages in-flight
    sendingMessages: {
      [room_id: string]: boolean;
    };

    /** is the list of room participants open and covering the message window? */
    isShowingRoomParticipantsList: boolean;

    /** the room id that was just closed */
    justClosedRoomId?: string;
  };

  // Per room unread state
  roomUnreadState: {
    [room_id: string]: RoomUnreadState | undefined;
  };

  // This slice will be stored in LS
  drafts: {
    // When you start a new chat but dont send a message
    participants: PapiUser[];

    // Any draft messages, one per room
    messages: {
      [room_id: string]: DraftMessage;
    };

    // We have to track which urls we have unfurled
    // as we only unfurl once to deal with removing attachments
    unfurlLog: {
      [room_id: string]: UnfurlLogEntry[];
    };

    /**
     * Track any deviation ids we've tried to fetch, but backend doesn't return.
     * Using this to make sure we don't try to fetch those ids again on every user input
     */
    deviationsNotFound: number[];
    unfurlResolving: boolean;
  };

  storage: {
    enterHandling: 'newLine' | 'send';
    canConnectToDamz: number | undefined;
  };

  // Cache of users current user cannot message
  denyUserList: {
    [username: string]: boolean;
  };
  damzActions: {
    deleteLeaveRoom: {
      isDeleting: boolean;
    };
    renameRoom: {
      isRenaming: boolean;
    };
  };

  /** both damz-wide and message search state: query and results */
  search: {
    q?: string;
    isSearchingMessages?: boolean;
    order?: 'default' | 'newest';
    results?: any[];
  };
  // Users who reacted to messages
  reactionUsers: {
    [messageId: string]: {
      [emoji_id: string]: {
        users: PapiUser[];
        isLoading: boolean;
        error: string | null;
      };
    };
  };
}

export interface RoomItem {
  room: Optional<
    Pick<
      Database['damz']['Functions']['get_conversations']['Returns'][number],
      | 'room_id'
      | 'name'
      | 'room_type'
      | 'updated_at'
      | 'owner_id'
      | 'user_count'
    >,
    'name'
  >;
  roomMeta: Pick<
    Database['damz']['Functions']['get_conversations']['Returns'][number],
    'flags'
  >;
  lastMsg?: {
    msg: string;
    user: PapiUser;
    deleted_at?: string;
    message_id?: string;
  };
  dmUser?: PapiUser;
  /** an array of two users (name, icon) that are members of a 'multi' room type */
  multiUsers?: {
    username: string;
    avatarSrc: string;
  }[];
  /** a full list/array of ALL users in the community channel or multi-user room */
  participants?: PapiUser[];
}

export interface MessageItem {
  msg: Pick<
    Database['damz']['Functions']['get_messages']['Returns'][number],
    | 'message_id'
    | 'format'
    | 'msg_type'
    | 'sent_at'
    | 'edit_at'
    | 'room_id'
    | 'edit_at'
    | 'deleted_at'
    | 'msg'
    | 'a_attachment'
    | 'b_attachment'
    | 'c_attachment'
    | 'd_attachment'
    | 'e_attachment'
    | 'f_attachment'
  > & {
    reactions: Record<
      string,
      { emoji_id: number; count: number; order: number }
    >;
  };
  user?: PapiUser;
}

export enum AttachmentType {
  ImageDeviation = 'imageDeviation',
  NonImageDeviation = 'nonImageDeviation',
  Video = 'video',
  File = 'file',
  Gif = 'gif',
  Quote = 'quote',
  Money = 'money',
  UploadProgress = 'uploadProgress',
}

interface BaseAttachment {
  link: string;
  attachmentType: AttachmentType;
  isMature?: boolean;
}

export interface ImageDeviationAttachment extends BaseAttachment {
  image: string;
  width: number;
  height: number;
}

export interface NonImageDeviationAttachment extends BaseAttachment {
  title?: string;
  nonImageType?:
    | 'journal'
    | 'literature'
    | 'tier'
    | 'commission'
    | 'status'
    | 'poll'
    | 'file';
}

export interface VideoAttachment extends BaseAttachment {
  isVideo: boolean;
}

export interface FileAttachment extends NonImageDeviationAttachment {
  type: string; // e.g., "xml", "pdf", etc.
}

export interface QuoteAttachment extends BaseAttachment {
  user: PapiUser;
  messageId: string;
  msg: string;
}

export interface GifAttachment extends ImageDeviationAttachment {
  mp4: string;
}

export interface MoneyAttachment extends BaseAttachment {
  image: string;
  width: number;
  height: number;
  title?: string;
  nonImageType?: 'tier' | 'commission';
}

export interface UploadProgressAttachment extends BaseAttachment {
  fileId: string;
  file: File;
  progress: number; // 0-100
  status: 'uploading' | 'error' | 'complete';
  error?: string;
}

export type MessageAttachment =
  | ImageDeviationAttachment
  | FileAttachment
  | NonImageDeviationAttachment
  | QuoteAttachment
  | VideoAttachment
  | GifAttachment
  | MoneyAttachment
  | UploadProgressAttachment;

export const RoomType = {
  DM: 1,
  MULTI: 2,
  COMMUNITY: 3,
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RoomType = keyof typeof RoomType;

/**
 * Mapping of RoomType enum values to BI event string representations
 */
export const ROOM_TYPE_TO_BI_STRING = {
  [RoomType.DM]: 'direct_message',
  [RoomType.MULTI]: 'group',
  [RoomType.COMMUNITY]: 'channel',
} as const;

export const RoomFlags = {
  all: 0,
  muted: 1,
  unread: 2,
  starred: 4,
  archived: 8,
  request: 16,
  watching: 32,
  supporters: 64,
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RoomFlags = keyof typeof RoomFlags;

export const RoomFlagNames = {
  all: 'all',
  muted: 'muted',
  unread: 'unread',
  starred: 'starred',
  archived: 'archived',
  request: 'request',
  watching: 'watching',
  supporters: 'supporters',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RoomFlagNames = keyof typeof RoomFlagNames;

// ------------------ api stuff
/** messageId and snippet object from /search/messages */
export type PapiDamzSearchMessagesResult = PapiDamzSearchMessages['results'][0];

/** above combined with associated RoomItems used for
 *  message search results as RoomItemLinks */
export interface PapiDamzSearchMessagesWithRoomData
  extends PapiDamzSearchMessagesResult {
  user: PapiUser;
  roomItem: RoomItem;
}
